import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Plus, Loader2, Trash2, ListChecks, Activity } from "lucide-react";

const TASK_TYPES = ["medicamento", "exame", "medida", "atividade", "sono", "retorno", "questionario", "geral"];

export default function PlanoTab({ patientId }) {
  const [tasks, setTasks] = useState([]);
  const [symptoms, setSymptoms] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({ title: "", type: "medicamento", due_date: "", notes: "" });

  const load = () => {
    api.get(`/patients/${patientId}/tasks`).then((r) => setTasks(r.data));
    api.get(`/patients/${patientId}/symptoms`).then((r) => setSymptoms(r.data));
  };
  useEffect(() => { load(); }, [patientId]);

  const save = async () => {
    if (!f.title.trim()) { toast.error("Informe a tarefa"); return; }
    setSaving(true);
    try {
      await api.post(`/patients/${patientId}/tasks`, { ...f, due_date: f.due_date || null });
      toast.success("Tarefa adicionada ao plano");
      setOpen(false); setF({ title: "", type: "medicamento", due_date: "", notes: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const toggle = async (t) => {
    const status = t.status === "concluida" ? "pendente" : "concluida";
    await api.put(`/patients/${patientId}/tasks/${t.id}?status=${status}`);
    setTasks((prev) => prev.map((x) => (x.id === t.id ? { ...x, status } : x)));
  };

  const remove = async (tid) => {
    await api.delete(`/patients/${patientId}/tasks/${tid}`);
    load();
  };

  const done = tasks.filter((t) => t.status === "concluida").length;
  const adherence = tasks.length ? Math.round((done / tasks.length) * 100) : 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-heading text-xl font-medium">Plano de adesão</h3>
          <p className="text-sm text-muted-foreground">Adesão: {done}/{tasks.length} · {adherence}%</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-task-btn"><Plus className="w-4 h-4 mr-2" /> Nova tarefa</Button>
          </DialogTrigger>
          <DialogContent data-testid="task-dialog">
            <DialogHeader>
              <DialogTitle>Adicionar tarefa ao plano</DialogTitle>
              <DialogDescription>O paciente vê e marca as tarefas no portal.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2"><Label>Tarefa</Label>
                <Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="task-title-input" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Tipo</Label>
                  <Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}>
                    <SelectTrigger data-testid="task-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{TASK_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Prazo</Label>
                  <Input type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} data-testid="task-due-input" /></div>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={save} disabled={saving} data-testid="save-task-btn">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Adicionar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {tasks.length === 0 ? (
        <Card className="p-8 text-center text-muted-foreground border-dashed">
          <ListChecks className="w-8 h-8 mx-auto mb-3 opacity-40" /> Nenhuma tarefa no plano.
        </Card>
      ) : (
        <div className="space-y-2">
          {tasks.map((t) => (
            <Card key={t.id} className="p-4 border-border shadow-none flex items-center gap-3" data-testid={`task-${t.id}`}>
              <Checkbox checked={t.status === "concluida"} onCheckedChange={() => toggle(t)} data-testid={`task-check-${t.id}`} />
              <div className="flex-1">
                <p className={`text-sm font-medium ${t.status === "concluida" ? "line-through text-muted-foreground" : ""}`}>{t.title}</p>
                <p className="text-xs text-muted-foreground">{t.type}{t.due_date ? ` · prazo ${t.due_date}` : ""}</p>
              </div>
              <Button size="icon" variant="ghost" onClick={() => remove(t.id)} data-testid={`delete-task-${t.id}`}>
                <Trash2 className="w-4 h-4 text-muted-foreground" />
              </Button>
            </Card>
          ))}
        </div>
      )}

      <div>
        <h4 className="font-heading font-medium mb-3 flex items-center gap-2"><Activity className="w-4 h-4 text-ai" /> Sintomas relatados</h4>
        {symptoms.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nenhum sintoma relatado pelo paciente.</p>
        ) : (
          <div className="space-y-2">
            {symptoms.map((s) => (
              <Card key={s.id} className="p-3 border-border shadow-none flex items-center justify-between" data-testid={`symptom-${s.id}`}>
                <div>
                  <p className="text-sm">{s.description}</p>
                  <p className="text-xs text-muted-foreground">{s.date} · relatado por {s.reported_by}</p>
                </div>
                {s.severity != null && <Badge variant="secondary">Intensidade {s.severity}</Badge>}
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
